// ============================================================================
// Дизайн-система — реактивный viewport (ds.display)
// ============================================================================
// РЕАКТИВНАЯ замена одноразовых isPhone()/isTablet() из core/mixins (они
// опрашивали matchMedia в момент вызова: повернул планшет — компонент не узнал).
// Брейкпоинты — из CSS-переменных дизайн-системы (--phone-landscape-max и др.,
// объявлены в styles/layout.scss) — ОДИН источник правды с CSS/JIT.
//
// API (всё реактивно, обновляется matchMedia-листенерами):
//   ds.display.phone / .tablet / .desktop / .xlDesktop / .xxlDesktop
//   ds.display.mobile   — phone ИЛИ tablet (главный флаг «мобильная раскладка»)
//   ds.display.name     — 'phone' | 'tablet' | 'desktop' | 'xl' | 'xxl'
//   ds.display.width    — px
//   ds.display.pwa      — standalone/minimal-ui режим
//
// SSR: window нет → все флаги false, width 0 (как и раньше у isPhone()).
// Инициализация ленивая — при первом обращении на клиенте (к этому моменту
// CSS с переменными брейкпоинтов уже применён).
import { reactive } from 'vue';

const isClientSide = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const state = reactive({
  flipPhone: false,
  phone: false,
  tabletPortrait: false,
  tabletLandscape: false,
  desktop: false,
  xlDesktop: false,
  xxlDesktop: false,
  pwa: false,
  width: 0,
});

const getCssVar = name => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

// [ключ state, построение media-query из CSS-переменных]
const queries = [
  ['flipPhone',       () => `(max-width: ${getCssVar('--flip-phone-max')})`],
  ['phone',           () => `(max-width: ${getCssVar('--phone-landscape-max')})`],
  ['tabletPortrait',  () => `(min-width: ${getCssVar('--tablet-portrait-min')}) and (max-width: ${getCssVar('--tablet-portrait-max')})`],
  ['tabletLandscape', () => `(min-width: ${getCssVar('--tablet-landscape-min')}) and (max-width: ${getCssVar('--tablet-landscape-max')})`],
  ['desktop',         () => `(min-width: ${getCssVar('--desktop-min')}) and (max-width: ${getCssVar('--desktop-max')})`],
  ['xlDesktop',       () => `(min-width: ${getCssVar('--xl-desktop-min')}) and (max-width: ${getCssVar('--xl-desktop-max')})`],
  ['xxlDesktop',      () => `(min-width: ${getCssVar('--xxl-desktop-min')})`],
  ['pwa',             () => '(display-mode: standalone), (display-mode: minimal-ui)'],
];

let initialized = false;

function ensureInit() {
  if (initialized || !isClientSide()) return;
  initialized = true;

  for (const [key, buildQuery] of queries) {
    const mq = window.matchMedia(buildQuery());
    state[key] = mq.matches;
    mq.addEventListener('change', e => { state[key] = e.matches; });
  }
  // iOS standalone (не покрывается display-mode)
  if (window.navigator.standalone === true) state.pwa = true;

  state.width = window.innerWidth;
  window.addEventListener('resize', () => { state.width = window.innerWidth; });
}

export const display = {
  get flipPhone()       { ensureInit(); return state.flipPhone; },
  get phone()           { ensureInit(); return state.phone; },
  get tabletPortrait()  { ensureInit(); return state.tabletPortrait; },
  get tabletLandscape() { ensureInit(); return state.tabletLandscape; },
  get tablet()          { ensureInit(); return state.tabletPortrait || state.tabletLandscape; },
  get desktop()         { ensureInit(); return state.desktop; },
  get xlDesktop()       { ensureInit(); return state.xlDesktop; },
  get xxlDesktop()      { ensureInit(); return state.xxlDesktop; },
  get pwa()             { ensureInit(); return state.pwa; },
  get width()           { ensureInit(); return state.width; },

  // Главный флаг «мобильная раскладка»: телефон или планшет
  get mobile() {
    ensureInit();
    return state.phone || state.tabletPortrait || state.tabletLandscape;
  },

  get name() {
    ensureInit();
    if (state.phone) return 'phone';
    if (state.tabletPortrait || state.tabletLandscape) return 'tablet';
    if (state.xxlDesktop) return 'xxl';
    if (state.xlDesktop) return 'xl';
    return 'desktop';
  },
};
